export type EspDialect =
  | "handlebars"
  | "liquid"
  | "django"
  | "ampscript"
  | "zephyr"
  | "twig";

export type EspFieldSource = "standard" | "custom";

/** Native Delivery has no `destinations` row, so a scope points at it by sentinel. */
export const NATIVE_DELIVERY_TARGET = "native_delivery";

/**
 * Bare braces only work for plain identifiers; a token with a space or dot needs
 * bracket lookup, which bare braces cannot express without a Liquid parse error.
 */
export function toLiquidExpression(token: string): string {
  if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(token)) return `{{ ${token} }}`;
  const quote = token.includes('"') ? "'" : '"';
  return `{{ [${quote}${token}${quote}] }}`;
}
// Handlebars ends a path at whitespace, so a dot-flattened name containing a
// space parses as a helper call. Segment literals are the only escape, and they
// cannot contain `]` — such a segment is unrepresentable, hence null.
const HANDLEBARS_BARE_SEGMENT = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

function toHandlebarsPath(name: string): string | null {
  const segments: string[] = [];
  for (const segment of name.split(".")) {
    if (HANDLEBARS_BARE_SEGMENT.test(segment)) {
      segments.push(segment);
    } else if (segment.includes("]")) {
      return null;
    } else {
      segments.push(`[${segment}]`);
    }
  }
  return segments.join(".");
}

// Liquid member access after Braze's `${…}`: bare segments take a dot, anything
// else bracket-quotes. Same rule `normalizeBrazeAttributeRefs` reads back.
const LIQUID_BARE_SEGMENT = /^[A-Za-z_][A-Za-z0-9_]*$/;

function toLiquidMemberAccess(name: string): string | null {
  if (LIQUID_BARE_SEGMENT.test(name)) return `.${name}`;
  // Bracket-quoting is not escaping: a `}}` closes the output tag early and a
  // segment carrying both quote characters has no valid Liquid literal.
  if (!MERGE_TAG_NAME.braze.test(name)) return null;
  if (name.includes('"') && name.includes("'")) return null;
  const quote = name.includes('"') ? "'" : '"';
  return `[${quote}${name}${quote}]`;
}

export const ESP_CATALOG_PROVIDERS = [
  "iterable",
  "braze",
  "klaviyo",
  "sfmc",
  // User fields only: `list/tag` has no snippet equivalent, so content blocks
  // stay uncatalogued and `buildSnippetReference` keeps returning null.
  "xtremepush",
] as const;

export type EspCatalogProvider = (typeof ESP_CATALOG_PROVIDERS)[number];

export function normalizeProviderType(providerType: string): string {
  return providerType === "braze_email_template" ? "braze" : providerType;
}

export interface EspDialectCapability {
  dialect: EspDialect;
  /** Null when the dialect cannot express the name — emitting a broken tag is worse. */
  buildMergeTag: (name: string, source: EspFieldSource) => string | null;
  buildSnippetReference: (name: string) => string | null;
  /** Catalog column the snippet reference is keyed on; display name when absent. */
  snippetReferenceKey?: "externalId";
}

const quoteSingle = (value: string): string =>
  value.replace(/\\/g, "\\\\").replace(/'/g, "\\'");

/**
 * The identifier set each provider accepts. None of these grammars has an escape
 * sequence, so emission validates instead of escaping. Each set is also what that
 * provider's read-back grammar matches, so what we emit is what we can parse.
 */
const SNIPPET_IDENTIFIER = {
  // Per Iterable's CreateSnippetRequest. Notably no dots.
  iterable: /^[A-Za-z0-9 _-]{1,100}$/,
  braze: /^[A-Za-z0-9_-]+$/,
  // A Content Builder asset id.
  sfmc: /^\d+$/,
  sailthru: /^[A-Za-z0-9 ._-]+$/,
} as const;

// Merge-tag names, same reasoning: these grammars lack escape sequences, so a
// name carrying the dialect's own delimiter is unrepresentable — validate.
const MERGE_TAG_NAME = {
  braze: /^[^{}]+$/,
  // Klaviyo standard fields are bare Django attribute access (`person.foo`).
  klaviyoStandard: /^[A-Za-z_][A-Za-z0-9_]*$/,
  // Custom uses `|lookup:'…'`, but `}}` / `{%` inside the quoted string still
  // close or open a tag early — reject delimiter characters the same way Braze does.
  klaviyo: /^[^{}%]+$/,
  sfmc: /^[^%]+$/,
  sailthru: /^[^{}]+$/,
  // Twig dotted attribute access. A name needing `attribute()` or `_context['…']`
  // to be reachable is rejected rather than emitted in a form we cannot read back.
  xtremepush: /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/,
} as const;

/**
 * Null rather than a throw: catalog refreshes emit thousands of blocks per run,
 * so one unrepresentable name must skip its row, not fail the whole page.
 */
function snippetIdentifier(identifier: string, allowed: RegExp): string | null {
  return allowed.test(identifier) ? identifier : null;
}

/**
 * Variables the ESP supplies at render time. The compiler maps these to the
 * target's native token and excludes them from requiredFields: sending our own
 * unsubscribe value would break the ESP's compliance tracking.
 *
 * Preferences-center equivalents are deliberately unmapped throughout — they
 * depend on customer-side configuration and render empty when unconfigured, so
 * that binding has to be explicit.
 */
export const SYSTEM_TOKENS_BY_TARGET = {
  iterable: {
    unsubscribe_url: "{{unsubscribeUrl}}",
  },
  braze: {
    unsubscribe_url: "{{${set_user_to_unsubscribed_url}}}",
  },
  sfmc: {
    // Both spellings map, so already-rewritten HTML doesn't end up as
    // AttributeValue("unsubscribeUrl").
    unsubscribe_url: "%%unsub_center_url%%",
    unsubscribeUrl: "%%unsub_center_url%%",
  },
  // Klaviyo's unsubscribe is a provider tag (`{% unsubscribe %}`), not a
  // merge-tag rewrite of `unsubscribe_url`. Sailthru has no mapped
  // unsubscribe token in-repo — leave both empty so we don't invent one.
  klaviyo: {},
  sailthru: {},
  // Xtremepush has no unsubscribe URL of its own to map: the documented pattern
  // builds one against the customer's own site from `{{ user.email_token }}`,
  // which we cannot know.
  xtremepush: {},
} as const;

export const ESP_DIALECTS: Record<string, EspDialectCapability> = {
  [NATIVE_DELIVERY_TARGET]: {
    dialect: "liquid",
    // ND resolves tokens against one merge-tag list with no standard/custom
    // split, so `source` says nothing here.
    buildMergeTag: (name) => toLiquidExpression(name),
    // ND blocks are referenced by id, so the snippet tools' ND arm builds each block's tag.
    buildSnippetReference: () => null,
  },
  iterable: {
    dialect: "handlebars",
    buildMergeTag: (name) => {
      const path = toHandlebarsPath(name);
      return path === null ? null : `{{${path}}}`;
    },
    buildSnippetReference: (name) => {
      const identifier = snippetIdentifier(name, SNIPPET_IDENTIFIER.iterable);
      return identifier === null ? null : `{{{snippet "${identifier}"}}}`;
    },
  },
  braze: {
    dialect: "liquid",
    buildMergeTag: (name, source) => {
      if (source === "standard") {
        return MERGE_TAG_NAME.braze.test(name) ? "{{${" + name + "}}}" : null;
      }
      // `${…}` names one attribute and member access follows it, but
      // `/custom_attributes` returns nested properties dot-joined — so split at
      // the first dot and emit the rest as Liquid member access. Docs-level
      // evidence only; Braze has no render endpoint to verify against.
      // Object-array leaves (needing `[0]`) are out of scope.
      const [head, ...rest] = name.split(".");
      if (!head || !MERGE_TAG_NAME.braze.test(head)) return null;
      const suffix: string[] = [];
      for (const segment of rest) {
        const access = toLiquidMemberAccess(segment);
        if (access === null) return null;
        suffix.push(access);
      }
      return "{{custom_attribute.${" + head + "}" + suffix.join("") + "}}";
    },
    // Braze's documented form. `{% content_block x %}` is not a Braze tag at all
    // and fails the Liquid lint as an unknown tag.
    buildSnippetReference: (name) => {
      const identifier = snippetIdentifier(name, SNIPPET_IDENTIFIER.braze);
      return identifier === null
        ? null
        : `{{content_blocks.\${${identifier}}}}`;
    },
  },
  klaviyo: {
    dialect: "django",
    buildMergeTag: (name, source) => {
      if (source === "standard") {
        return MERGE_TAG_NAME.klaviyoStandard.test(name)
          ? `{{ person.${name} }}`
          : null;
      }
      return MERGE_TAG_NAME.klaviyo.test(name)
        ? `{{ person|lookup:'${quoteSingle(name)}' }}`
        : null;
    },
    buildSnippetReference: () => null,
  },
  sfmc: {
    dialect: "ampscript",
    buildMergeTag: (name) =>
      MERGE_TAG_NAME.sfmc.test(name) ? `%%${name}%%` : null,
    snippetReferenceKey: "externalId",
    // ContentBlockByID keys on the numeric asset id stored as external_id.
    // The empty impression region and `false` keep a missing block from hard-failing the send.
    buildSnippetReference: (assetId) => {
      const identifier = snippetIdentifier(assetId, SNIPPET_IDENTIFIER.sfmc);
      return identifier === null
        ? null
        : `%%=ContentBlockByID("${identifier}", "", false)=%%`;
    },
  },
  sailthru: {
    dialect: "zephyr",
    buildMergeTag: (name) =>
      MERGE_TAG_NAME.sailthru.test(name) ? `{${name}}` : null,
    buildSnippetReference: (name) => {
      const identifier = snippetIdentifier(name, SNIPPET_IDENTIFIER.sailthru);
      return identifier === null ? null : `{include "${identifier}"}`;
    },
  },
  xtremepush: {
    dialect: "twig",
    // System attributes sit under `user.`; everything else is a bare attribute
    // name resolved against the profile.
    buildMergeTag: (name, source) => {
      if (!MERGE_TAG_NAME.xtremepush.test(name)) return null;
      return source === "standard" ? `{{ user.${name} }}` : `{{ ${name} }}`;
    },
    // Snippets exist and render (`{{ snippet('name') }}`), but the external API
    // cannot list or create them, so we can never hold a block we have verified
    // is there. A reference to a missing snippet fails at send time with no
    // earlier signal, so emit nothing rather than a tag we cannot back up.
    buildSnippetReference: () => null,
  },
};

export function getEspDialectCapability(
  providerType: string,
): EspDialectCapability | null {
  // Own-property check, not `?? null`: a providerType of `constructor` or
  // `toString` would otherwise resolve to an inherited value and pass the gate.
  const key = normalizeProviderType(providerType);
  return Object.hasOwn(ESP_DIALECTS, key) ? (ESP_DIALECTS[key] ?? null) : null;
}

/**
 * The delimiters a dialect wraps a merge tag in. Stripping them is what turns a
 * *rendered value* tag into the *expression* form a conditional needs.
 */
const MERGE_TAG_DELIMITERS: Record<
  EspDialect,
  { open: string; close: string }
> = {
  handlebars: { open: "{{", close: "}}" },
  liquid: { open: "{{", close: "}}" },
  django: { open: "{{", close: "}}" },
  ampscript: { open: "%%", close: "%%" },
  zephyr: { open: "{", close: "}" },
  twig: { open: "{{", close: "}}" },
};

/**
 * How a catalog field is referenced *inside* a condition, where the block tags
 * already supply delimiters: Braze renders `{{${first_name}}}`, the predicate
 * reads `{% if ${first_name} == "x" %}`. Null when the tag isn't delimited as
 * its dialect delimits merge tags — guessing would emit unparseable syntax.
 */
export function toConditionReference(
  dialect: EspDialect,
  mergeTag: string,
): string | null {
  const { open, close } = MERGE_TAG_DELIMITERS[dialect];
  const tag = mergeTag.trim();
  if (tag.length <= open.length + close.length) return null;
  if (!tag.startsWith(open) || !tag.endsWith(close)) return null;
  const inner = tag.slice(open.length, tag.length - close.length).trim();
  if (inner === "") return null;
  // A delimiter left inside means the tag carried a layer this dialect can't
  // nest: Sailthru's single braces read a Liquid `{{x}}` as the expression
  // `{x}`. Braze's `${…}` survives — it is neither `{{` nor `}}`.
  if (inner.includes(open) || inner.includes(close)) return null;
  return inner;
}

/**
 * The non-editable if/else block tags a predicate sits inside. `compile.ts`
 * emits these around each branch at deploy time, so authoring shows the syntax
 * the deploy produces — change an emitter's block tags and these must change
 * too; nothing enforces the pairing yet.
 */
export type EspConditionAdornments = {
  /** Prefix/suffix around the first row's predicate. */
  first: { prefix: string; suffix: string };
  /** Prefix/suffix around every later row's predicate. Same as `first` for
   * dialects whose waterfall nests plain ifs instead of chaining elsifs. */
  rest: { prefix: string; suffix: string };
  /** Chip shown on the "Everyone else" fallback row. */
  fallbackChip: string;
};

// Handlebars is absent on purpose: Iterable equality compiles to `{{#ifEq}}`,
// not `{{#if}}`, so there is no single wrapper to show.
export const ESP_CONDITION_ADORNMENTS: Partial<
  Record<EspDialect, EspConditionAdornments>
> = {
  // Braze: if/elsif/else chain.
  liquid: {
    first: { prefix: "{% if", suffix: "%}" },
    rest: { prefix: "{% elsif", suffix: "%}" },
    fallbackChip: "{% else %}",
  },
  // Klaviyo: nested {% if %}/{% else %} (Django has no elsif).
  django: {
    first: { prefix: "{% if", suffix: "%}" },
    rest: { prefix: "{% if", suffix: "%}" },
    fallbackChip: "{% else %}",
  },
  // SFMC: nested IF/ELSE/ENDIF blocks.
  ampscript: {
    first: { prefix: "%%[ IF", suffix: "THEN ]%%" },
    rest: { prefix: "%%[ IF", suffix: "THEN ]%%" },
    fallbackChip: "%%[ ELSE ]%%",
  },
  // Sailthru: nested {if}/{else}/{/if}.
  zephyr: {
    first: { prefix: "{if", suffix: "}" },
    rest: { prefix: "{if", suffix: "}" },
    fallbackChip: "{else}",
  },
  // Xtremepush (Twig 3.8): if/elseif/else. Spelled `elseif`, not Liquid's `elsif`.
  twig: {
    first: { prefix: "{% if", suffix: "%}" },
    rest: { prefix: "{% elseif", suffix: "%}" },
    fallbackChip: "{% else %}",
  },
};

/**
 * The reference tag for a cached block, preferring the one the refresh stored.
 * A rebuilt tag uses the column that provider's reference keys on.
 * SFMC's ContentBlockByID keys on the asset id stored as external_id.
 */
export function buildCachedSnippetReference(
  capability: EspDialectCapability | null,
  block: { name: string; externalId?: string; referenceTag?: string | null },
): string | null {
  if (block.referenceTag) return block.referenceTag;
  if (!capability) return null;
  const key =
    capability.snippetReferenceKey === "externalId"
      ? block.externalId
      : block.name;
  // No key means no verifiable reference: fall through to the reference note
  // rather than emitting a tag that silently resolves to nothing.
  return key ? capability.buildSnippetReference(key) : null;
}

export function isEspCatalogProvider(providerType: string): boolean {
  const normalized = normalizeProviderType(providerType);
  return ESP_CATALOG_PROVIDERS.some((provider) => provider === normalized);
}
